import { ChronoUnit, LocalDate } from '@js-joda/core';
import { useUpdateEffect } from 'react-use';

import { EntitlementType } from '@dreryk/drerykweb-medical-dictionaries-api';
import {
   Patient,
   PatientEntitlementArmyDisabledCreateCommand,
   PatientEntitlementBloodDonorCreateCommand,
   PatientEntitlementInjuredVeteranCreateCommand,
   PatientEntitlementPregnancyCreateCommand,
   PatientEntitlementValidityOverlapsExistingEntitlementFault,
   PatientEntitlementWarDisabledCreateCommand,
} from '@dreryk/egabinet-patient-frontend-api';

import { useCqrsCommand, UseCqrsCommandResult } from '@dreryk/command-query-react-common';
import { assertUnreachable, useFormController } from '@dreryk/react-common';
import { dispatchSnackbarNegativeEvent } from '@dreryk/react-components';

import { patientEntitlementsTranslationKeys } from '../../translations';
import { PregnancyDateRange } from '../confirmingDocument/PregnancyDateFields/PregnancyDateFields.types';
import { toValidityDateRangeValue } from '../patientEntitlements.utils';

import {
   OnPatientEntitlementDocumentCreated,
   PatientEntitlementCreateCommandType,
   PatientEntitlementCreateFormBean,
   PatientEntitlementCreateFormController,
} from './PatientEntitlementCreateForm.types';

type PatientEntitlementFormControllerOptions = {
   entitlementType: EntitlementType;
};

export const PREGNANCY_PERIOD_IN_MONTHS = 9;

export const defaultPregnancyValidityDateRange = (): PregnancyDateRange => {
   const startDate = LocalDate.now();
   const endDate = startDate.plus(PREGNANCY_PERIOD_IN_MONTHS, ChronoUnit.MONTHS);
   return { start: startDate, end: endDate };
};

export const usePatientEntitlementCreateFormController = (
   options: PatientEntitlementFormControllerOptions
): PatientEntitlementCreateFormController => {
   const controller = useFormController<PatientEntitlementCreateFormBean>({
      confirmingDocumentType: null,
      confirmingDocumentNumber: null,
      validityDateRange: options.entitlementType === 'C' ? defaultPregnancyValidityDateRange() : null,
   });
   useUpdateEffect(controller.reset, [options.entitlementType]);
   return controller;
};

export const choosePatientEntitlementCreateCommand = (
   entitlementType: EntitlementType
): PatientEntitlementCreateCommandType => {
   switch (entitlementType) {
      case 'WE':
         return new PatientEntitlementInjuredVeteranCreateCommand();
      case 'IB':
         return new PatientEntitlementWarDisabledCreateCommand();
      case 'IW':
         return new PatientEntitlementArmyDisabledCreateCommand();
      case 'ZK':
         return new PatientEntitlementBloodDonorCreateCommand();
      case 'C':
         return new PatientEntitlementPregnancyCreateCommand();
      case 'DN':
      case 'DZ':
      case 'S':
      case 'IN':
         throw new Error(`Not supported type of patient entitlement document: ${entitlementType}`);
      default:
         return assertUnreachable(entitlementType);
   }
};

type CreatePatientEntitlementCommandOptions = {
   entitlementType: EntitlementType;
   patientId: Patient['id'];
   onPatientEntitlementDocumentCreated: OnPatientEntitlementDocumentCreated;
};

export const useCqrsCreatePatientEntitlementCommand = (
   options: CreatePatientEntitlementCommandOptions
): UseCqrsCommandResult<
   PatientEntitlementCreateCommandType,
   PatientEntitlementCreateFormBean,
   PatientEntitlementValidityOverlapsExistingEntitlementFault
> => {
   return useCqrsCommand(
      (parameters) => {
         const command = choosePatientEntitlementCreateCommand(options.entitlementType);
         command.patientId = options.patientId;
         command.confirmingDocumentTypeId = parameters.confirmingDocumentType!.id;
         command.confirmingDocumentNumber = parameters.confirmingDocumentNumber!;
         command.validityDateRange = toValidityDateRangeValue(parameters.validityDateRange)!;
         return command;
      },
      {
         onSuccess: () => options.onPatientEntitlementDocumentCreated(options.entitlementType),
         onFault: {
            'egabinet-patient-frontend-api:PatientEntitlementValidityOverlapsExistingEntitlementFault': () =>
               dispatchSnackbarNegativeEvent(
                  patientEntitlementsTranslationKeys.patientEntitlementValidityOverlapsExistingEntitlementFault
               ),
         },
      }
   );
};
